<template>
  <CCard class="contract-minimum-charge">
    <CCardHeader>
      <div class="section-heading">
        <div>
          <strong>最低月額</strong>
          <p>この会社に適用する最低月額を設定します。個別に金額を変更することも可能です。</p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>
      <CAlert v-if="successMessage" color="success">{{ successMessage }}</CAlert>

      <div v-if="loading" class="contract-minimum-charge__loading">読み込んでいます…</div>

      <!--
        **契約の無い会社には、選択肢も保存も出さない**（ContractIntroPrice と同じ判定・同じ文）。
        back は契約の無い会社に何も返さず、保存も「対象の契約情報が見つかりません。」で断る。
        選択肢を出すと、押せるのに必ず断られる画面になる。
      -->
      <CAlert v-else-if="loadedAccountId !== null && !linked" color="warning">
        この契約企業は、まだ契約が登録されていません。
        先に契約を保存してから設定してください。
      </CAlert>

      <!--
        **読めた会社の値があるときだけ、選択肢と保存を出す。**
        読めなかったときに空の選択肢を出すと、「プランに従う」のまま保存できてしまい、
        入っていた「当てない」や別の金額を消す。
      -->
      <template v-else-if="loadedAccountId !== null">
        <p v-if="!canEdit" class="text-muted">最低月額の変更には「契約情報を更新」権限が必要です。</p>

        <div class="minimum-choices">
          <label class="minimum-choice">
            <input
              v-model="form.mode"
              type="radio"
              name="minimum-mode"
              value="PLAN"
              :disabled="!canEdit"
            />
            <span>{{ planChoiceText }}</span>
          </label>
          <label class="minimum-choice">
            <input
              v-model="form.mode"
              type="radio"
              name="minimum-mode"
              value="NONE"
              :disabled="!canEdit"
            />
            <span>最低月額を適用しない</span>
          </label>
          <label class="minimum-choice">
            <input
              v-model="form.mode"
              type="radio"
              name="minimum-mode"
              value="CUSTOM"
              :disabled="!canEdit"
            />
            <span id="minimum-custom-label">金額を指定する</span>
          </label>

          <!--
            **金額の欄は「別の金額」を選んだときだけ出す。**他を選んでいるのに
            前の金額が見えていると、その金額が効いていると読める。
            **step は付けない**（min=1 が刻みの起点になり、step=100 だと 10,000円が
            刻みに合わない。請求管理の「プランの料金」の欄と同じ1刻み）。
          -->
          <div v-if="form.mode === 'CUSTOM'" class="minimum-custom">
            <CInput
              id="minimum-custom-amount"
              v-model="form.customAmount"
              type="number"
              min="1"
              :max="max"
              aria-labelledby="minimum-custom-label"
              :disabled="!canEdit"
              description="1円〜10,000,000円の範囲で、半角整数（小数点なし）で入力してください。"
            />
          </div>
        </div>

        <div class="minimum-remarks">
          <label for="minimum-remarks">備考</label>
          <CInput id="minimum-remarks" v-model.trim="form.remarks" maxlength="200" :disabled="!canEdit"/>
        </div>

        <!--
          **当てない月を、保存する前に書いておく。**初期特別価格の期間中・無料プラン・
          無料試用中の月には当てない（設計の決まり 5・6）。
          **今月が初期特別価格の期間なら、目に付く色にする。**「設定したのに
          今月の見込みが最低月額にならない」と読まれるのを防ぐ。
        -->
        <p
          class="minimum-note"
          :class="{ 'minimum-note--warning': introPriceActive }"
        >初期特別価格の適用期間中、無料プラン、無料試用中の月は最低月額を適用しません。</p>

        <div v-if="canEdit" class="contract-minimum-charge__actions">
          <CButton
            type="button"
            color="primary"
            :disabled="saving"
            @click="save"
          >{{ saving ? '保存しています…' : '保存する' }}</CButton>
        </div>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import api from '@/services/accessManagement'
import { safeResponseMessage, safeErrorDigest } from '@/utils/safeResponseMessage'

/** 選べる3つ。back も同じ3つしか受け付けない（それ以外は「適用方法を正しく選択」の文で断る）。 */
const MODES = ['PLAN', 'NONE', 'CUSTOM']

/**
 * 金額の範囲。**1円〜1,000万円。**0円は「当てない」と区別が付かないので
 * 受け付けない（当てないときは「当てない」を選ぶ）。back と同じ範囲で断る。
 */
const MIN_AMOUNT = 1
const MAX_AMOUNT = 10000000

/**
 * 選択肢1の文。**{金額} に、今月の請求に使うプランの最低月額が入る**（月の途中でプランを替えた月は月初のプラン。監査 E-3）（無いときは「設定なし」の語）。
 * {金額} は文の中に1つだけ置く（planChoiceText は最初の1つしか差し替えない）。
 */
const PLAN_CHOICE_TEMPLATE = 'プランに従う（{金額}）'
const PLAN_HAS_NO_MINIMUM = '設定なし'

/**
 * 最低月額の、契約ごとの設定（0107）。
 *
 * <h2>何を決めるか</h2>
 *
 * その会社の月額（基本料＋単価×人数）が届かないときに、どの額まで下支えするか。
 *   PLAN   … プランの最低月額に従う（設定の行が無いときもこれ）
 *   NONE   … この会社には当てない
 *   CUSTOM … この会社だけ別の金額にする
 * 利用者の「ありなしで柔軟に」（2026-09-26）。設計：C:\biz\claude\最低月額の下支え_設計_2026-09-26.md
 *
 * <h2>どこから見えるか</h2>
 *
 * 運営メニュー「PREIKA運営」→「契約企業一覧」→ 会社の［管理］→ 初期特別価格の隣のタブ。
 *
 * <h2>確定済みの請求は動かない</h2>
 *
 * 月次確定は、当てた最低月額と差額を請求ごとに写して持つ。ここを変えて効くのは、
 * まだ確定していない月だけ（取り消して出し直す月は、そのときの設定で計算し直す）。
 *
 * <h2>ContractIntroPrice との違い</h2>
 *
 * **同じ会社を選び直したときも読み直す**（reloadKey）。あちらは会社の番号しか
 * 見ていないので、同じ会社の［管理］をもう一度押しても前の画面の値が残る。
 */
export default {
  name: 'ContractMinimumCharge',

  props: {
    accountId: { type: Number, required: true },
    /** 契約を変えられるか（CONTRACT_UPDATE）。false なら読むだけ。 */
    canEdit: { type: Boolean, default: false },
    /**
     * 読み直しの番号。親は「会社を選び直した回（accountRequestSeq）」と
     * 「同じ会社の［管理］の中で契約・初期特別価格を保存した回」を組にして渡す。
     * **同じ会社でも、変われば読み直す。**
     */
    reloadKey: { type: [Number, String], default: 0 }
  },

  data () {
    return {
      form: this.空の入力(),
      planMinimumMonthlyAmount: null,
      introPriceActive: false,
      // 契約のある会社か。back は契約の無い会社に何も返さない（ContractIntroPrice と同じ）。
      linked: false,
      // **いま画面に出ている値は、どの会社のものか。**（ContractIntroPrice と同じ）
      loadedAccountId: null,
      loading: true,
      saving: false,
      errorMessage: '',
      successMessage: '',
      max: MAX_AMOUNT
    }
  },

  computed: {
    /** 読み直しのきっかけ。**会社の番号と、選び直しの番号の組。** */
    loadKey () {
      return this.accountId + ':' + this.reloadKey
    },

    /** 選択肢1の文に、今月の請求に使うプランの最低月額を差し込む。 */
    planChoiceText () {
      const amount = this.planMinimumMonthlyAmount === null
        ? PLAN_HAS_NO_MINIMUM
        : this.円(this.planMinimumMonthlyAmount)
      return PLAN_CHOICE_TEMPLATE.replace('{金額}', amount)
    }
  },

  watch: {
    loadKey: {
      immediate: true,
      handler () {
        /*
         * **別の会社へ移ったときだけ、前の会社の保存を捨てる**（ContractIntroPrice と同じ）。
         * 同じ会社の選び直しでは捨てない。捨てると、保存が通ったのに
         * 「保存しました」も読み直しも来ず、古い値が出たままになる。
         */
        if (this.$_watchedAccountId !== this.accountId) {
          this.$_saveOp = (this.$_saveOp || 0) + 1
          this.saving = false
        }
        this.$_watchedAccountId = this.accountId
        this.load()
      }
    }
  },

  methods: {
    空の入力 () {
      return { mode: 'PLAN', customAmount: '', remarks: '' }
    },

    文字に (value) {
      return value === null || value === undefined ? '' : String(value)
    },

    数か空 (value) {
      return value === null || value === undefined || String(value).trim() === ''
        ? null : Number(value)
    },

    円 (value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '—'
      return number.toLocaleString('ja-JP') + '円'
    },

    /**
     * **会社を切り替えたら、前の会社の返事は捨てる**（ContractIntroPrice の監査 F2 と同じ）。
     *
     * 読み始めたらすぐ入力を空にし、どの会社の値かの印（loadedAccountId）も外す。
     * **返事が来るまで、前の会社の値は画面にも保存にも残らない。**
     */
    async load () {
      // 画面に出さない数なので data に置かない（描き直しを起こさない）。
      const seq = this.$_loadSeq = (this.$_loadSeq || 0) + 1
      const target = this.accountId
      this.loading = true
      this.loadedAccountId = null
      this.errorMessage = ''
      this.successMessage = ''
      this.form = this.空の入力()
      this.planMinimumMonthlyAmount = null
      this.introPriceActive = false
      this.linked = false
      const stale = () => seq !== this.$_loadSeq || target !== this.accountId
      try {
        const response = await api.getContractMinimumCharge(target)
        if (stale()) return
        // **契約の無い会社は、空（null・空の本文）が返る。**ContractIntroPrice の linked と同じ判定。
        this.linked = !!response
        const current = response && typeof response === 'object' ? response : {}
        this.form = {
          // 設定の行が無ければ PLAN（back もそう返す）。
          mode: current.mode === null || current.mode === undefined ? 'PLAN' : String(current.mode),
          customAmount: this.文字に(current.customAmount),
          remarks: current.remarks || ''
        }
        // 最低月額が無いプランは、鍵ごと返らないことがある。どちらも「なし」。
        this.planMinimumMonthlyAmount = this.数か空(current.planMinimumMonthlyAmount)
        this.introPriceActive = current.introPriceActive === true
        this.loadedAccountId = target
      } catch (error) {
        if (stale()) return
        this.errorMessage = '最低月額を読み込めませんでした。'
        this.$emit('load-error', safeErrorDigest(error))
      } finally {
        if (!stale()) this.loading = false
      }
    },

    /** 保存してよいか。読み込み中・別の会社の値・権限なしでは送らない。 */
    canSend () {
      return this.canEdit && !this.saving && !this.loading && this.linked
        && this.loadedAccountId !== null && this.loadedAccountId === this.accountId
    },

    /** 送る前に、サーバと同じ断り方で止める（文をそろえる）。 */
    whatIsWrong () {
      if (MODES.indexOf(this.form.mode) < 0) {
        return '最低月額の適用方法を正しく選択してください。'
      }
      if (this.form.mode === 'CUSTOM') {
        const amount = this.数か空(this.form.customAmount)
        // **小数は送らない**（front #784 の監査。請求書の明細の合計と小計が1円ずれる）。
        // 文は請求管理の「プランの料金」・［プランを追加］と同じ。
        if (amount === null || !Number.isInteger(amount)
          || amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
          return '最低月額は1円〜10,000,000円の範囲内で、整数（小数点なし）で入力してください。'
        }
      }
      return ''
    },

    async save () {
      if (!this.canSend()) return
      const wrong = this.whatIsWrong()
      if (wrong) {
        this.errorMessage = wrong
        this.successMessage = ''
        return
      }
      await this.send({
        accountId: this.loadedAccountId,
        mode: this.form.mode,
        // **金額は「別の金額」のときだけ送る。**他のときに送ると、選んでいない額が残る。
        customAmount: this.form.mode === 'CUSTOM' ? this.数か空(this.form.customAmount) : null,
        remarks: this.form.remarks
      })
    },

    /**
     * **保存を始めた会社を覚えておく**（ContractIntroPrice の監査 F2 の残りと同じ）。
     *
     * 終わったときに別の会社へ移っていたら、いまの画面には何もしない
     * （成功でも失敗でも）。同じ会社のままなら、入っている値を読み直して見せる。
     */
    async send (payload) {
      const target = this.loadedAccountId
      const op = this.$_saveOp = (this.$_saveOp || 0) + 1
      const moved = () => op !== this.$_saveOp || target !== this.accountId
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await api.replaceContractMinimumCharge(payload)
        if (moved()) return
        // **保存できた値を読み直す。**送ったつもりの値ではなく、入っている値を見せる。
        await this.load()
        if (moved()) return
        this.successMessage = '最低月額を保存しました。'
      } catch (error) {
        if (moved()) return
        this.errorMessage = safeResponseMessage(
          error, '最低月額を保存できませんでした。時間をおいて再度お試しください。')
      } finally {
        if (!moved()) this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.contract-minimum-charge { min-width: 0; margin-bottom: 18px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; width: 100%; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }

.contract-minimum-charge__loading { color: #5c6873; }

.minimum-choices {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.minimum-choice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  font-weight: 400;
  cursor: pointer;
}

.minimum-choice input { margin-top: 0.3rem; }

/* 選択肢3の下に字下げして置く。どの選択肢の金額かを並びで分からせる。 */
.minimum-custom { max-width: 16rem; margin-left: 1.5rem; }

.minimum-remarks { max-width: 28rem; }
.minimum-remarks label { display: block; font-weight: 600; font-size: 0.8rem; }

.minimum-note {
  margin: 0.75rem 0 0;
  color: #6a7f8c;
  font-size: 0.8rem;
}

.minimum-note--warning { color: #b54708; }

.contract-minimum-charge__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}
</style>
